import { ReactElement, useState } from 'react';
import { Device } from '@/api';
import {
    UIButton,
    UISection,
    UISectionContent,
    UISectionHeader,
    UIText,
    UITooltip,
    useUIConfirmationModal
} from '@/ui';
import {
    useCancelDeviceFallAlertAction,
    useCancelDeviceTiltAlertAction
} from '../actions';
import { useDeviceAlertLicenses } from '../queries';
import { useDevicesTranslation } from '../hooks';
import { SectionRow } from './section-row';
import { PendingBadge } from './pending-badge';
import { FallAlertModal } from './fall-alert-modal';
import { TiltAlertModal } from './tilt-alert-modal';

export type DeviceAlertsSectionProps = {
    device: Device;
    /** Whether the current user may edit this device. */
    canEdit: boolean;
};

type Translate = ReturnType<typeof useDevicesTranslation>['t'];

/** A single named config value, used to diff a config-only change. */
type Field = { label: string; value: string };

/** One side of a state transition: the reported config or a pending target. */
type SideView = {
    enabled: boolean;
    /** Structured settings for field-level diffing; empty when off. */
    fields: Field[];
};

/**
 * "Alerts" section of the device page: fall and tilt detection configuration.
 * Shows the device's current reported config plus any pending change as a
 * two-line view — status (Enabled/Disabled) on the first line, config on the
 * second — with the change arrow on whichever line actually changes, each with a
 * cancel action. Geofence is configured separately.
 */
export function DeviceAlertsSection(
    props: DeviceAlertsSectionProps
): ReactElement {
    const { device, canEdit } = props;
    const { t } = useDevicesTranslation();
    const confirmationModal = useUIConfirmationModal();
    const { cancelDeviceFallAlert } = useCancelDeviceFallAlertAction({
        deviceId: device.id
    });
    const { cancelDeviceTiltAlert } = useCancelDeviceTiltAlertAction({
        deviceId: device.id
    });
    const licenses = useDeviceAlertLicenses({
        companyId: device.companyId ?? '',
        deviceId: device.id
    });

    const [isFallOpen, setIsFallOpen] = useState(false);
    const [isTiltOpen, setIsTiltOpen] = useState(false);

    const pendingFall = device.commands?.find(
        (c) => c.type === 'set_fall_alert' && c.status === 'pending'
    );
    const pendingTilt = device.commands?.find(
        (c) => c.type === 'set_tilt_alert' && c.status === 'pending'
    );

    function confirmCancel(run: () => Promise<unknown>) {
        confirmationModal.open({
            title: t('shared.devices.cancel_pending_confirm_title'),
            confirmText: t('shared.devices.cancel_pending_confirm'),
            confirmVariant: 'danger',
            cancelText: t('shared.labels.cancel'),
            onConfirm: () => {
                confirmationModal.setLoading(true);
                run()
                    .catch(() => {}) // Error handled in the action
                    .finally(() => confirmationModal.close());
            },
            onCancel: () => confirmationModal.close()
        });
    }

    const fallSide = (
        enabled: boolean,
        sensitivity: number | undefined
    ): SideView =>
        enabled && sensitivity !== undefined
            ? {
                  enabled,
                  fields: [
                      {
                          label: t('shared.devices.fall_sensitivity_label'),
                          value: String(sensitivity)
                      }
                  ]
              }
            : { enabled, fields: [] };

    const tiltSide = (
        enabled: boolean,
        angle: number | undefined,
        time: number | undefined
    ): SideView =>
        enabled && angle !== undefined && time !== undefined
            ? {
                  enabled,
                  fields: [
                      {
                          label: t('shared.devices.tilt_angle_label'),
                          value: t('shared.devices.tilt_angle_value', {
                              value: angle
                          })
                      },
                      {
                          label: t('shared.devices.tilt_time_label'),
                          value: t('shared.devices.tilt_time_value', {
                              value: time
                          })
                      }
                  ]
              }
            : { enabled, fields: [] };

    const fall = device.config?.fallAlert;
    const fallEnabled = fall?.enabled ?? false;
    const fallCurrent = fallSide(fallEnabled, fall?.sensitivity);
    const fallTarget = pendingFall
        ? fallSide(
              !!pendingFall.data.fallAlertEnabled,
              pendingFall.data.fallAlertSensitivity
          )
        : undefined;

    const tilt = device.config?.tiltAlert;
    const tiltEnabled = tilt?.enabled ?? false;
    const tiltCurrent = tiltSide(tiltEnabled, tilt?.angle, tilt?.timeThreshold);
    const tiltTarget = pendingTilt
        ? tiltSide(
              !!pendingTilt.data.tiltAlertEnabled,
              pendingTilt.data.tiltAlertAngle,
              pendingTilt.data.tiltAlertTimeThreshold
          )
        : undefined;

    // A feature is "blocked" when this device has it off and there is no spare
    // addon license. Show "(Upgrade required)" when the company owns no addon at
    // all, otherwise "(Limit reached)". An already-enabled device is never
    // blocked, so its Edit button stays available to turn it off. Until usage
    // has loaded the entitlement is unknown, so we don't flag it as blocked
    // (the Edit button is disabled separately while loading).
    const fallBlocked =
        !fallEnabled && !licenses.loading && !licenses.fall.canEnableNew;
    const tiltBlocked =
        !tiltEnabled && !licenses.loading && !licenses.tilt.canEnableNew;

    function blockedAccessory(limit: number): ReactElement {
        return (
            <UIText medium type="error">
                {limit === 0
                    ? t('shared.devices.upgrade_required')
                    : t('shared.devices.limit_reached')}
            </UIText>
        );
    }

    return (
        <>
            <UISection>
                <UISectionHeader
                    title={t('components.device_alerts_section.title')}
                />
                <UISectionContent>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 24
                        }}
                    >
                        <SectionRow
                            label={t('shared.devices.fall_label')}
                            description={t('shared.devices.fall_description')}
                            labelAccessory={
                                fallBlocked
                                    ? blockedAccessory(licenses.fall.limit)
                                    : undefined
                            }
                        >
                            <AlertRowContent
                                current={fallCurrent}
                                target={fallTarget}
                                cancel={
                                    fallTarget
                                        ? {
                                              onCancel: () =>
                                                  confirmCancel(
                                                      cancelDeviceFallAlert
                                                  )
                                          }
                                        : undefined
                                }
                                canEdit={canEdit}
                                editDisabled={fallBlocked || licenses.loading}
                                editText={t('shared.labels.edit')}
                                onEdit={() => setIsFallOpen(true)}
                            />
                        </SectionRow>

                        <SectionRow
                            label={t('shared.devices.tilt_label')}
                            description={t('shared.devices.tilt_description')}
                            labelAccessory={
                                tiltBlocked
                                    ? blockedAccessory(licenses.tilt.limit)
                                    : undefined
                            }
                        >
                            <AlertRowContent
                                current={tiltCurrent}
                                target={tiltTarget}
                                cancel={
                                    tiltTarget
                                        ? {
                                              onCancel: () =>
                                                  confirmCancel(
                                                      cancelDeviceTiltAlert
                                                  )
                                          }
                                        : undefined
                                }
                                canEdit={canEdit}
                                editDisabled={tiltBlocked || licenses.loading}
                                editText={t('shared.labels.edit')}
                                onEdit={() => setIsTiltOpen(true)}
                            />
                        </SectionRow>
                    </div>
                </UISectionContent>
            </UISection>
            <FallAlertModal
                device={device}
                isOpen={isFallOpen}
                onClose={() => setIsFallOpen(false)}
            />
            <TiltAlertModal
                device={device}
                isOpen={isTiltOpen}
                onClose={() => setIsTiltOpen(false)}
            />
        </>
    );
}

/**
 * List every setting, showing changed ones as "label: from → to" and unchanged
 * ones as static "label: value" context, so both settings always appear.
 */
function fieldsText(current: SideView, target: SideView, t: Translate): string {
    return current.fields
        .map((field, index) => {
            const to = target.fields[index]?.value;
            return to !== undefined && to !== field.value
                ? t('shared.devices.field_value_change', {
                      label: field.label,
                      value: field.value,
                      to
                  })
                : t('shared.devices.field_value', {
                      label: field.label,
                      value: field.value
                  });
        })
        .join(t('shared.devices.field_separator'));
}

/** List one side's settings as static "label: value" context. */
function labeledFields(side: SideView, t: Translate): string {
    return fieldsText(side, side, t);
}

/**
 * The config line (line 2). Shows a per-field diff when both sides are enabled,
 * otherwise the config of whichever side has it (target when enabling, current
 * otherwise — so a disabling change keeps the old config visible).
 */
function configLine(
    current: SideView,
    t: Translate,
    target?: SideView
): string {
    if (!target) {
        return current.enabled ? labeledFields(current, t) : '';
    }
    if (current.enabled && target.enabled) {
        return fieldsText(current, target, t);
    }
    return target.enabled
        ? labeledFields(target, t)
        : labeledFields(current, t);
}

/**
 * Two-line view of an alert's state: status (Enabled/Disabled) on line 1, config
 * (Sensitivity / Angle etc.) on line 2. When a change is pending, the arrow sits
 * on whichever line actually changes — line 1 for enable/disable, line 2 for a
 * value change (per changed field).
 */
function AlertStatusView(props: {
    current: SideView;
    target?: SideView;
}): ReactElement {
    const { current, target } = props;
    const { t } = useDevicesTranslation();

    const label = (side: SideView): string =>
        side.enabled ? t('shared.labels.enabled') : t('shared.labels.disabled');

    const statusChanged = !!target && target.enabled !== current.enabled;
    const configText = configLine(current, t, target);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    flexWrap: 'wrap'
                }}
            >
                {statusChanged ? (
                    <>
                        <UIText medium type="disabled">
                            {label(current)}
                        </UIText>
                        <UIText type="default2">→</UIText>
                        <UIText medium type={target.enabled ? 'link' : 'error'}>
                            {label(target)}
                        </UIText>
                    </>
                ) : (
                    <UIText medium type={current.enabled ? 'link' : 'error'}>
                        {label(current)}
                    </UIText>
                )}
            </div>
            {configText && <UIText type="default2">{configText}</UIText>}
        </div>
    );
}

function AlertRowContent(props: {
    current: SideView;
    target?: SideView;
    cancel?: { onCancel: () => void };
    canEdit: boolean;
    editDisabled: boolean;
    editText: string;
    onEdit: () => void;
}): ReactElement {
    const { current, target, cancel, canEdit, editDisabled, editText, onEdit } =
        props;
    const { t } = useDevicesTranslation();

    return (
        <div
            style={{
                display: 'flex',
                flex: 1,
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                flexWrap: 'wrap'
            }}
        >
            {target ? (
                <>
                    <AlertStatusView current={current} target={target} />
                    <PendingBadge
                        label={t('shared.labels.pending')}
                        tooltip={
                            canEdit
                                ? t('shared.devices.cancel_pending_confirm')
                                : undefined
                        }
                        onCancel={canEdit ? cancel?.onCancel : undefined}
                    />
                </>
            ) : (
                <>
                    <AlertStatusView current={current} />
                    {canEdit && (
                        <UITooltip text={editText}>
                            <UIButton
                                leftIcon="edit-2"
                                variant="textDefault"
                                type="button"
                                customPadding={6}
                                disabled={editDisabled}
                                onClick={onEdit}
                            />
                        </UITooltip>
                    )}
                </>
            )}
        </div>
    );
}
